/**
 * Genera un PDF A4 con una rejilla de stickers personalizados listos para
 * imprimir.
 *
 * Flujo:
 *  1. Para cada sticker, creamos un canvas off-screen de alta resolución.
 *  2. Dibujamos la imagen base, esperamos a que cargue, y superponemos el
 *     texto del usuario usando la misma lógica `computeStickerTextLayout`
 *     que el editor y la preview, para que el PDF sea píxel-consistente con
 *     lo que el usuario ve.
 *  3. Componemos el PDF en A4 con una rejilla (por defecto 2x3 con tamaños
 *     físicos cómodos para recortar).
 *
 * Requisitos de fuentes: las fuentes personalizadas deben estar cargadas en
 * el documento antes de llamar a esta función (usamos `document.fonts.ready`).
 *
 * API:
 *   generateStickerPdf({ stickers, name, fileName, copiesPerSticker })
 */

import jsPDF from "jspdf";
import {
	computeStickerTextLayout,
	parseFontFamilyName,
	bezierPointAt,
	bezierTangentAngleAt,
	sampleBezierArcLength,
	tFromArcLength,
} from "./fitText";

const loadImage = (src) =>
	new Promise((resolve, reject) => {
		const img = new Image();
		img.crossOrigin = "anonymous";
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error(`No se pudo cargar la imagen ${src}`));
		img.src = src;
	});

const waitForFont = async (fontFamily) => {
	if (typeof document === "undefined" || !document.fonts) return;
	const name = parseFontFamilyName(fontFamily);
	if (!name) return;
	try {
		await document.fonts.load(`16px "${name}"`);
	} catch {
		/* noop */
	}
};

/**
 * Renderiza un sticker con su texto en un canvas off-screen y devuelve el
 * dataURL PNG. `targetWidthPx` controla la resolución (mayor = más nítido
 * al imprimir, pero más pesado).
 */
export const renderStickerToDataUrl = async (
	sticker,
	value,
	targetWidthPx = 1024,
) => {
	await waitForFont(sticker.text?.fontFamily);
	const img = await loadImage(sticker.baseImage);

	/* El canvas usa el aspectRatio DECLARADO en el catálogo (la "caja" del sticker).
	   La imagen se dibuja con object-fit "contain" dentro de esa caja, igual que
	   hacen StickerPreview (CSS) y Sticker.js (Konva). Así el texto se posiciona
	   coherentemente con lo que el usuario ve en el editor. */
	const declaredAspect = sticker.aspectRatio || 1;
	const W = targetWidthPx;
	const H = Math.round(W * declaredAspect);

	const canvas = document.createElement("canvas");
	canvas.width = W;
	canvas.height = H;
	const ctx = canvas.getContext("2d");

	/* "contain": respetar el aspecto natural de la imagen dentro de W×H. */
	const natW = img.naturalWidth || img.width || W;
	const natH = img.naturalHeight || img.height || H;
	const imgAspect = natW / natH;
	const boxAspect = W / H;
	let dw, dh, dx, dy;
	if (imgAspect > boxAspect) {
		dw = W;
		dh = W / imgAspect;
		dx = 0;
		dy = (H - dh) / 2;
	} else {
		dh = H;
		dw = H * imgAspect;
		dx = (W - dw) / 2;
		dy = 0;
	}
	ctx.drawImage(img, dx, dy, dw, dh);

	if (sticker.text) {
		const layout = computeStickerTextLayout(value, sticker.text, W, H);
		drawStickerText(ctx, layout);
	}

	return canvas.toDataURL("image/png");
};

/**
 * Dibuja el texto del sticker en el contexto 2D usando la configuración
 * calculada por `computeStickerTextLayout`.
 *
 * Nota: `letterSpacing` no es nativo en canvas 2D; si el sticker lo necesita
 * de forma crítica, se podría dibujar char a char. Para nuestros stickers
 * actuales (letterSpacing pequeño o 0) el resultado es visualmente idéntico.
 */
const drawStickerTextVertical = (ctx, layout) => {
	const {
		text,
		rect,
		fontSize,
		fontFamily,
		fontWeight,
		fontStyle,
		color,
		stroke,
		strokeWidth,
		shadow,
		align,
		verticalLineHeight,
	} = layout;

	const chars = Array.from(text);
	const lineHeight = verticalLineHeight ?? 1.12;
	const lh = fontSize * lineHeight;
	const n = Math.max(1, chars.length);
	const totalH = n * lh;

	const cssFontStyle = fontStyle === "italic" ? "italic " : "";
	ctx.font = `${cssFontStyle}${fontWeight} ${fontSize}px ${fontFamily}`;
	ctx.textBaseline = "middle";

	let cx =
		align === "left"
			? rect.x
			: align === "right"
				? rect.x + rect.width
				: rect.x + rect.width / 2;

	const startY = rect.y + (rect.height - totalH) / 2 + lh / 2;

	for (let i = 0; i < chars.length; i += 1) {
		const cy = startY + i * lh;
		if (shadow) {
			ctx.shadowColor = shadow.color;
			ctx.shadowOffsetX = shadow.offsetX;
			ctx.shadowOffsetY = shadow.offsetY;
			ctx.shadowBlur = shadow.blur;
		} else {
			ctx.shadowColor = "transparent";
		}
		ctx.textAlign =
			align === "left" ? "left" : align === "right" ? "right" : "center";
		if (stroke && strokeWidth > 0) {
			ctx.lineJoin = "round";
			ctx.miterLimit = 2;
			ctx.strokeStyle = stroke;
			ctx.lineWidth = strokeWidth * 2;
			ctx.strokeText(chars[i], cx, cy);
		}
		ctx.shadowColor = "transparent";
		ctx.fillStyle = color;
		ctx.fillText(chars[i], cx, cy);
	}
};

/** Línea horizontal centrada en (0,0), ya en coords de grupo; scaleX opcional aplicado antes. */
const drawStickerTextVerticalRotated = (ctx, layout) => {
	const {
		text,
		rect,
		fontSize,
		scaleX,
		fontFamily,
		fontWeight,
		fontStyle,
		color,
		stroke,
		strokeWidth,
		shadow,
		letterSpacing,
	} = layout;

	const cx = rect.x + rect.width / 2;
	const cy = rect.y + rect.height / 2;
	const sx = scaleX ?? 1;

	ctx.save();
	ctx.translate(cx, cy);
	ctx.rotate(Math.PI / 2);
	ctx.scale(sx, 1);

	const cssFontStyle = fontStyle === "italic" ? "italic " : "";
	ctx.font = `${cssFontStyle}${fontWeight} ${fontSize}px ${fontFamily}`;
	ctx.textBaseline = "middle";
	ctx.textAlign = "left";

	const chars = Array.from(text);
	const sp = letterSpacing || 0;
	let totalW = 0;
	const widths = chars.map((c) => {
		const w = ctx.measureText(c).width;
		totalW += w;
		return w;
	});
	totalW += sp * Math.max(0, chars.length - 1);

	let penX = -totalW / 2;

	const drawShadowStrokeFill = (ch, x, y) => {
		if (shadow) {
			ctx.shadowColor = shadow.color;
			ctx.shadowOffsetX = shadow.offsetX;
			ctx.shadowOffsetY = shadow.offsetY;
			ctx.shadowBlur = shadow.blur;
		} else {
			ctx.shadowColor = "transparent";
		}
		if (stroke && strokeWidth > 0) {
			ctx.lineJoin = "round";
			ctx.miterLimit = 2;
			ctx.strokeStyle = stroke;
			ctx.lineWidth = strokeWidth * 2;
			ctx.strokeText(ch, x, y);
		}
		ctx.shadowColor = "transparent";
		ctx.fillStyle = color;
		ctx.fillText(ch, x, y);
	};

	for (let i = 0; i < chars.length; i += 1) {
		const ch = chars[i];
		const w = widths[i];
		drawShadowStrokeFill(ch, penX, 0);
		penX += w + (i < chars.length - 1 ? sp : 0);
	}

	ctx.restore();
};

const drawStickerText = (ctx, layout) => {
	if (layout.curve) {
		drawStickerTextOnCurve(ctx, layout);
		return;
	}
	if (layout.verticalRotate) {
		drawStickerTextVerticalRotated(ctx, layout);
		return;
	}
	if (layout.vertical) {
		drawStickerTextVertical(ctx, layout);
		return;
	}
	const {
		text,
		rect,
		fontSize,
		scaleX,
		fontFamily,
		fontWeight,
		fontStyle,
		color,
		stroke,
		strokeWidth,
		shadow,
		align,
	} = layout;

	ctx.save();

	const cssFontStyle = fontStyle === "italic" ? "italic " : "";
	ctx.font = `${cssFontStyle}${fontWeight} ${fontSize}px ${fontFamily}`;
	ctx.textBaseline = "middle";
	ctx.textAlign = align;

	const cx =
		align === "left"
			? rect.x
			: align === "right"
				? rect.x + rect.width
				: rect.x + rect.width / 2;
	const cy = rect.y + rect.height / 2;

	// Compresión horizontal para nombres largos
	if (scaleX && scaleX !== 1) {
		ctx.translate(cx, cy);
		ctx.scale(scaleX, 1);
		ctx.translate(-cx, -cy);
	}

	if (shadow) {
		ctx.shadowColor = shadow.color;
		ctx.shadowOffsetX = shadow.offsetX;
		ctx.shadowOffsetY = shadow.offsetY;
		ctx.shadowBlur = shadow.blur;
	}

	if (stroke && strokeWidth > 0) {
		ctx.lineJoin = "round";
		ctx.miterLimit = 2;
		ctx.strokeStyle = stroke;
		// El stroke de canvas pinta la mitad hacia fuera y mitad hacia dentro,
		// por eso Konva y la preview DOM usan "paint-order: stroke fill".
		// Duplicamos para que visualmente tenga el mismo peso.
		ctx.lineWidth = strokeWidth * 2;
		ctx.strokeText(text, cx, cy);
	}

	// Relleno sin sombra para que la sombra solo afecte al contorno
	ctx.shadowColor = "transparent";
	ctx.fillStyle = color;
	ctx.fillText(text, cx, cy);

	ctx.restore();
};

/**
 * Dibuja texto a lo largo de una curva cuadrática Bezier (la definida en
 * `layout.curve`). Cada carácter se traslada a su posición y se rota siguiendo
 * la tangente de la curva en ese punto.
 *
 * Distribución: centrado por defecto, o alineado a izquierda/derecha. La
 * posición de cada carácter se calcula por arc-length real (no por t) para
 * que la tipografía no se apelotone en los extremos.
 */
const drawStickerTextOnCurve = (ctx, layout) => {
	const {
		text,
		curve,
		fontSize,
		fontFamily,
		fontWeight,
		fontStyle,
		color,
		stroke,
		strokeWidth,
		shadow,
		align,
		letterSpacing,
	} = layout;

	const cssFontStyle = fontStyle === "italic" ? "italic " : "";

	ctx.save();
	ctx.font = `${cssFontStyle}${fontWeight} ${fontSize}px ${fontFamily}`;
	ctx.textBaseline = "alphabetic";
	ctx.textAlign = "center";

	const chars = Array.from(text);
	const charWidths = chars.map((c) => ctx.measureText(c).width);
	const totalWidth =
		charWidths.reduce((a, b) => a + b, 0) +
		(letterSpacing || 0) * Math.max(0, chars.length - 1);

	const arc = sampleBezierArcLength(curve.p0, curve.cp, curve.p1, 120);

	// Offset inicial según alineación.
	let startOffset;
	if (align === "left") startOffset = 0;
	else if (align === "right") startOffset = arc.total - totalWidth;
	else startOffset = (arc.total - totalWidth) / 2;

	let cursor = startOffset;
	for (let i = 0; i < chars.length; i += 1) {
		const w = charWidths[i];
		const centerDist = cursor + w / 2;
		const t = tFromArcLength(arc, centerDist);
		const pt = bezierPointAt(curve.p0, curve.cp, curve.p1, t);
		const angle = bezierTangentAngleAt(curve.p0, curve.cp, curve.p1, t);

		ctx.save();
		ctx.translate(pt.x, pt.y);
		ctx.rotate(angle);

		if (shadow) {
			ctx.shadowColor = shadow.color;
			ctx.shadowOffsetX = shadow.offsetX;
			ctx.shadowOffsetY = shadow.offsetY;
			ctx.shadowBlur = shadow.blur;
		}
		if (stroke && strokeWidth > 0) {
			ctx.lineJoin = "round";
			ctx.miterLimit = 2;
			ctx.strokeStyle = stroke;
			ctx.lineWidth = strokeWidth * 2;
			ctx.strokeText(chars[i], 0, 0);
		}
		ctx.shadowColor = "transparent";
		ctx.fillStyle = color;
		ctx.fillText(chars[i], 0, 0);
		ctx.restore();

		cursor += w + (letterSpacing || 0);
	}
	ctx.restore();
};

/**
 * Genera un PDF A4 con los stickers dispuestos en una rejilla. Devuelve la
 * instancia jsPDF y, en modo "download", dispara la descarga.
 *
 * @param {Object} opts
 * @param {Array}  opts.stickers            Lista de stickers del país.
 * @param {string} opts.name                Nombre a estampar en todos.
 * @param {string} [opts.fileName]          Nombre del archivo resultante.
 * @param {string} [opts.title]             Título del PDF (p. ej. "Japón").
 * @param {number} [opts.copiesPerSticker]  Copias de cada sticker (por defecto 1).
 * @param {boolean}[opts.triggerDownload]   Si true, descarga; si false, devuelve el doc.
 * @param {number} [opts.targetWidthPx]     Resolución de cada sticker rasterizado.
 * @returns {Promise<jsPDF>}
 */
export const generateStickerPdf = async ({
	stickers,
	name,
	fileName = "stickers.pdf",
	title = "Stickers personalizados",
	copiesPerSticker = 1,
	triggerDownload = true,
	targetWidthPx = 1024,
}) => {
	if (!stickers || stickers.length === 0) {
		throw new Error("No hay stickers para generar");
	}

	// Nos aseguramos de que TODAS las fuentes de los stickers estén cargadas.
	const uniqueFonts = Array.from(
		new Set(stickers.map((s) => s.text?.fontFamily).filter(Boolean)),
	);
	await Promise.all(uniqueFonts.map((f) => waitForFont(f)));

	// Rasterizamos cada sticker una sola vez (la misma imagen se reutiliza
	// si hay más de una copia).
	const rendered = await Promise.all(
		stickers.map(async (s) => ({
			sticker: s,
			dataUrl: await renderStickerToDataUrl(s, name, targetWidthPx),
		})),
	);

	const doc = new jsPDF({
		unit: "mm",
		format: "a4",
		orientation: "portrait",
	});

	// Layout A4: 210 x 297 mm. Rejilla 3 columnas ⇒ ~15 stickers/página.
	const PAGE_W = 210;
	const PAGE_H = 297;
	const MARGIN = 10;
	const TITLE_H = 14;
	const GAP = 4;
	const LABEL_H = 5;
	const COLS = 3;

	const contentW = PAGE_W - MARGIN * 2;
	const cellW = (contentW - GAP * (COLS - 1)) / COLS;
	const cellH = cellW;

	let cursorX = MARGIN;
	let cursorY = MARGIN + TITLE_H;
	let colIndex = 0;

	const drawHeader = () => {
		doc.setFont("helvetica", "bold");
		doc.setFontSize(13);
		doc.setTextColor(15, 23, 42);
		doc.text(title, MARGIN, MARGIN + 5);

		doc.setFont("helvetica", "normal");
		doc.setFontSize(8);
		doc.setTextColor(100, 116, 139);
		doc.text(
			"Imprime en A4, recorta y pega donde quieras ✂",
			MARGIN,
			MARGIN + 10,
		);
	};

	const drawFooter = () => {
		doc.setFont("helvetica", "normal");
		doc.setFontSize(7);
		doc.setTextColor(160, 174, 192);
		doc.text(
			"Creado con Viajes Molones  ·  guias.viajesmolones.com",
			PAGE_W / 2,
			PAGE_H - MARGIN / 2,
			{ align: "center" },
		);
	};

	drawHeader();
	drawFooter();

	// Construimos la secuencia (sticker x copias)
	const queue = [];
	rendered.forEach((r) => {
		for (let i = 0; i < copiesPerSticker; i += 1) queue.push(r);
	});

	const rowH = cellH + LABEL_H + GAP;

	for (const { sticker, dataUrl, realAspect } of queue) {
		if (cursorY + cellH + LABEL_H > PAGE_H - MARGIN) {
			doc.addPage();
			drawHeader();
			drawFooter();
			cursorX = MARGIN;
			cursorY = MARGIN + TITLE_H;
			colIndex = 0;
		}

		/* Usar el aspect ratio real del PNG rasterizado (alto/ancho). */
		const aspect = realAspect || sticker.aspectRatio || 1;
		let imgW;
		let imgH;
		if (aspect <= 1) {
			imgW = cellW;
			imgH = cellW * aspect;
		} else {
			imgH = cellH;
			imgW = cellH / aspect;
		}

		const cellX = cursorX;
		const cellY = cursorY;
		const imgX = cellX + (cellW - imgW) / 2;
		const imgY = cellY + (cellH - imgH) / 2;

		doc.setDrawColor(203, 213, 225);
		doc.setLineDashPattern([1.2, 1.2], 0);
		doc.setLineWidth(0.15);
		doc.rect(cellX - 1, cellY - 1, cellW + 2, cellH + 2);
		doc.setLineDashPattern([], 0);

		doc.addImage(dataUrl, "PNG", imgX, imgY, imgW, imgH, undefined, "FAST");

		doc.setFont("helvetica", "normal");
		doc.setFontSize(6);
		doc.setTextColor(148, 163, 184);
		doc.text(sticker.title || "", cellX, cellY + cellH + 2, {
			baseline: "top",
		});

		colIndex += 1;
		if (colIndex >= COLS) {
			colIndex = 0;
			cursorX = MARGIN;
			cursorY += rowH;
		} else {
			cursorX += cellW + GAP;
		}
	}

	if (triggerDownload) {
		doc.save(fileName);
	}
	return doc;
};
